import { intersection, isEqual } from 'lodash';
import { Component, computed, inject, input, InputSignal, OnDestroy, signal } from '@angular/core';
import { Store } from '@ngxs/store';
import { combineLatest, defer, Subject, timer } from 'rxjs';
import { debounce, distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';

import { BaseCommonClientRequest, ClientRequestIdentityHelper } from '@aiwyn/common/client-requests';
import { DATA_SELECTOR_INJECTION_TOKEN, LoadableHelper, UploadFileJob } from '@aiwyn/shared';
import { ConfigService, LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';
import { AuthService } from '@aiwyn/auth';
import {
  ClientRequestDocumentUploadModel,
  ClientRequestItemStatus,
  RequestDocument,
  RequestSubmissionFile,
  RequestSubmissionFileStatus,
} from '@aiwyn/api';

import {
  ClientRequestsState,
  ClientRequestSubmitFileData,
  StageFilesForClientRequest,
  SubmitFilesForClientRequest,
} from '../../../../../../../state';
import { getOriginalBoxFileName } from '../../../../../../../models';

import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FileDisplay } from '../../../file-item/file-item.model';
import { MatDialog } from '@angular/material/dialog';
import {
  ConfirmationModalComponent,
  ConfirmationPayload,
} from 'src/app/shared/containers/dialog/confirmation/confirmation-modal.component';
import { ClientRequestPanelSelectors } from '../../../../client-request-panel.state';

@Component({
  selector: 'awn-document-item-content',
  templateUrl: './document-item-content.component.html',
  styleUrl: './document-item-content.component.scss',
  providers: [
    {
      provide: DATA_SELECTOR_INJECTION_TOKEN,
      useValue: 'document-item-content',
    },
  ],
  standalone: false,
})
export class DocumentItemContentComponent implements OnDestroy {
  protected readonly ClientRequestItemStatus = ClientRequestItemStatus;
  public dataSelector = inject(DATA_SELECTOR_INJECTION_TOKEN);

  // TODO Replace with single store selector
  private storeSubmissionFileDataMapSignal = this._store.selectSignal(ClientRequestsState.getSubmissionFileDataMap);
  private storeMetadataLoadableSignal = this._store.selectSignal(ClientRequestsState.getSelectedClientRequestMetadataLoadable);
  private storeFlaggedFileIdsSignal = this._store.selectSignal(ClientRequestsState.getFlaggedFileIds);

  private readonly _destroy$ = new Subject<void>();

  documentItem: InputSignal<RequestDocument> = input();
  instance: InputSignal<BaseCommonClientRequest<ClientRequestDocumentUploadModel>> = input();

  readonly: InputSignal<boolean> = input();

  // LaunchDarkly flag for file flagging feature
  isFileFlaggingEnabled = toSignal(this._launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_FILE_FLAGGING), {
    initialValue: false,
  });

  // LaunchDarkly flag for IN_REVIEW items editing
  isInReviewEditingEnabled = toSignal(this._launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_IN_REVIEW_ITEMS_EDITING), {
    initialValue: false,
  });

  // Item restriction check
  private readonly _authService = inject(AuthService);
  private contactId = toSignal(this._authService.getUser().pipe(map(user => user?.getContactId())));
  private isItemRestrictionsEnabled = toSignal(
    this._launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_CLIENT_REQUEST_ITEM_RESTRICTIONS),
    { initialValue: false },
  );

  protected isItemRestricted = computed(() => {
    const item = this.documentItem();
    const flagEnabled = this.isItemRestrictionsEnabled();
    const contactId = this.contactId();
    return flagEnabled && item?.restricted === true && !item.assignees?.some(a => a.contactId === contactId);
  });

  // Computed signal for whether editing/uploading is allowed
  isEditingAllowed = computed(() => {
    if (this.isItemRestricted()) {
      return false;
    }

    const document = this.documentItem();
    const isInReviewEditingEnabled = this.isInReviewEditingEnabled();

    if (isInReviewEditingEnabled) {
      // New behavior: Allow editing for all statuses except DONE
      return document?.status !== ClientRequestItemStatus.DONE;
    } else {
      // Original behavior: Disallow editing for IN_REVIEW and DONE
      return document?.status !== ClientRequestItemStatus.DONE && document?.status !== ClientRequestItemStatus.IN_REVIEW;
    }
  });

  jobId$ = defer(() => this.instance().groupIdentity$).pipe(
    map((identity) => ClientRequestIdentityHelper.parseGroupIdentity(identity)),
    map((parsedIdentity) => parsedIdentity.id),
  );

  files = computed<FileDisplay[]>(() => {
    const submissionFileDataMap = this.storeSubmissionFileDataMapSignal();
    const metadataLoadable = this.storeMetadataLoadableSignal();
    const flaggedFileIds = this.storeFlaggedFileIdsSignal();

    if (!metadataLoadable || !submissionFileDataMap || !this.documentItem()) {
      return [];
    }

    const metadata = LoadableHelper.getData(metadataLoadable);

    const filteredFiles: RequestSubmissionFile[] = metadata?.files?.filter((outerFile) =>
      this.documentItem()?.files.some((file) => {
        return (file.file?.['isMasked'] ?? false)
          ? file.file['requestSubmissionFileId'] === outerFile.id
          : (file.fileId ?? file.file.id) === outerFile.fileId;
      }),
    );

    if (!filteredFiles?.length) {
      return [];
    }

    // TODO: Find better placement, inherited from previous approach
    // Trigger empty check when files change (e.g., after unlinking) - only if feature flag is enabled
    if (this.isInReviewEditingEnabled() && this.documentItem()?.status === ClientRequestItemStatus.IN_REVIEW) {
      this.emptyCheckSubject$.next();
    }

    // Map files using submission file data from selector
    return filteredFiles.map((itemSubmissionFile) => {
      const originalFileName = getOriginalBoxFileName(itemSubmissionFile?.file?.name);
      const fileExtensionIndex = originalFileName?.lastIndexOf('.');
      const maskedFileExtension = itemSubmissionFile.file['extension'];

      // Find matching submission file data by fileId from map
      const compositeKey = ClientRequestsState.getSubmissionFileCompositeKey(itemSubmissionFile, this.documentItem()?.id);
      const submissionFileDataItem = submissionFileDataMap.get(compositeKey);

      const isFlaggedFile = flaggedFileIds.includes(itemSubmissionFile.file?.id);

      return {
        ...(itemSubmissionFile.file ?? {}),
        wasAutoCategorized: itemSubmissionFile?.status === RequestSubmissionFileStatus.AUTO_CATEGORIZED,
        name: originalFileName?.slice(0, fileExtensionIndex),
        extension: maskedFileExtension ?? originalFileName?.slice(fileExtensionIndex + 1),
        isFlagged: isFlaggedFile === true ? true : (submissionFileDataItem?.isFlagged ?? false),
        submissionFileId: itemSubmissionFile.id,
        flaggedAt: submissionFileDataItem?.flaggedAt,
        isMasked: itemSubmissionFile.file['isMasked'],
        createdByContact: submissionFileDataItem?.createdByContact,
        createdAt: submissionFileDataItem?.createdAt,
        version: submissionFileDataItem?.version ?? 1,
      } as FileDisplay;
    });
  });

  acceptedFileTypes: string[] = [];
  isLoading: Record<number, boolean> = {}; // Map with fileId as a key and a boolean to show whether that file is downloading data
  replacementFileLoading: Record<number, boolean> = {}; // Map with fileId as a key and a boolean to show whether that file is being replaced

  // Replacement-related properties
  private readonly _triggerFileSelection$ = new Subject<void>();
  readonly triggerFileSelection$ = this._triggerFileSelection$.asObservable();
  selectedFileForReplacement: FileDisplay | null = null;

  protected stagedFilesForThisDocumentIds = signal<string[]>([]);

  // Track if document is empty and show error
  protected hasEmptyDocumentError = signal(false);
  private emptyCheckSubject$ = new Subject<void>();

  constructor(
    private readonly _configService: ConfigService,
    private readonly _launchDarklyService: LaunchDarklyService,
    private readonly _store: Store,
    private readonly _dialog: MatDialog,
  ) {
    this.acceptedFileTypes = this._configService.clientRequestFileTypeWhitelist;

    // Set up empty document check with 1 second debounce
    this.emptyCheckSubject$
      .pipe(
        debounce(() => timer(300)),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this.checkIfDocumentEmpty();
      });

    combineLatest([this._store.select(ClientRequestPanelSelectors.getUploadedFileJobIds), toObservable(this.stagedFilesForThisDocumentIds)])
      .pipe(
        distinctUntilChanged((prev, curr) => isEqual(prev[0], curr[0]) && isEqual(prev[1], curr[1])),
        map(([files, stagedFileJobIds]) => intersection(files, stagedFileJobIds)),
        filter((idsToRemove) => idsToRemove.length > 0),
        takeUntil(this._destroy$),
      )
      .subscribe((idsToRemove) => {
        return this.stagedFilesForThisDocumentIds.update((ids) => ids.filter((id) => !idsToRemove.includes(id)));
      });
  }

  private checkIfDocumentEmpty(): void {
    const currentDocument = this.documentItem();
    const isInReviewEditingEnabled = this.isInReviewEditingEnabled();

    // Only check if feature flag is enabled and document is IN_REVIEW and has no files
    if (!isInReviewEditingEnabled || currentDocument?.status !== ClientRequestItemStatus.IN_REVIEW) {
      this.hasEmptyDocumentError.set(false);
      return;
    }

    const hasFiles = currentDocument.files && currentDocument.files.length > 0;
    const hasStagedFiles = this.stagedFilesForThisDocumentIds().length > 0;
    const isMarkedAsNotApplicable = currentDocument.isNotApplicable;

    if (this.hasEmptyDocumentError() && !hasFiles && !hasStagedFiles) {
      return;
    }

    if (!hasFiles && !hasStagedFiles && !isMarkedAsNotApplicable) {
      this.hasEmptyDocumentError.set(true);
      this.showEmptyDocumentModal();
    } else {
      this.hasEmptyDocumentError.set(false);
    }
  }

  private showEmptyDocumentModal(): void {
    this._dialog.open<ConfirmationModalComponent, ConfirmationPayload>(ConfirmationModalComponent, {
      data: {
        title: 'Request Cannot Be Empty',
        message: 'You cannot leave this request empty',
        confirmButtonText: 'OK',
        confirmButtonColor: 'negative',
      },
    });
  }

  onUploadFilesStart(files: UploadFileJob[]): void {
    // stage files (will be uploaded to our DB once DMS upload succeeds)
    const jobIds = files.map((f) => f.jobId);
    this.stagedFilesForThisDocumentIds.set([...this.stagedFilesForThisDocumentIds(), ...jobIds]);
    this._store.dispatch(new StageFilesForClientRequest(jobIds));

    // Clear error when files are being uploaded
    this.hasEmptyDocumentError.set(false);
  }

  onUploadFilesComplete(files: UploadFileJob[]): void {
    // submit files
    const marshaledFiles: ClientRequestSubmitFileData[] = files.map((file) => ({
      jobId: file.jobId,
      documentIds: [this.documentItem().id] as number[],
    }));

    this._store.dispatch(new SubmitFilesForClientRequest(marshaledFiles));

    // Trigger empty check after file completion
    this.emptyCheckSubject$.next();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._triggerFileSelection$.complete();
  }
}
